import PolicyHeaderIcon from "app/assets/IconComponent/PolicyHeaderIcon";
import AvatarWithName from "v2/common/AvatarWithName";
import { getSessionStore } from "app/utils/sessionStore";
import { THEME } from "app/utils/types";
import { TrendingUp } from "lucide-react";

interface PolicyRow {
  id: string;
  insuredName: string;
  insuredAvatar?: string;
  policyNumber: string;
  accountValue: string;
  surrenderValue: string;
  benefit: string;
  ownerName: string;
  ownerLogo?: string;
  beneficiary: string;
}

const MOCK_POLICIES: PolicyRow[] = [
  {
    id: "1",
    insuredName: "James Smith",
    insuredAvatar: "",
    policyNumber: "L99887665",
    accountValue: "$111K",
    surrenderValue: "$94K",
    benefit: "$2.35M",
    ownerName: "ABC Logistic, Inc.",
    ownerLogo: "",
    beneficiary: "ABC Services LLC",
  },
  {
    id: "2",
    insuredName: "James Smith",
    insuredAvatar: "",
    policyNumber: "L99887665",
    accountValue: "$111K",
    surrenderValue: "$94K",
    benefit: "$2.35M",
    ownerName: "ABC Logistic, Inc.",
    ownerLogo: "",
    beneficiary: "ABC Services LLC",
  },
];

const COLUMNS: { key: keyof PolicyRow; label: string; width: string }[] = [
  { key: "insuredName", label: "Insured", width: "18%" },
  { key: "policyNumber", label: "Policy #", width: "15%" },
  { key: "accountValue", label: "Account Value", width: "13%" },
  { key: "surrenderValue", label: "Surrender Value", width: "13%" },
  { key: "benefit", label: "Benefit", width: "10%" },
  { key: "ownerName", label: "Owner", width: "18%" },
  { key: "beneficiary", label: "Beneficiary", width: "13%" },
];

const PolicyBadge = () => (
  <div className="w-8 h-8 rounded-full border border-[2px] border-[#22C55E] p-2 flex items-center justify-center">
    <div className="w-full h-full rounded-full bg-[#22C55E] shadow-sm flex items-center justify-center">
      <TrendingUp size={13} strokeWidth={2.5} color="#fff" />
    </div>
  </div>
);

export type { PolicyRow };

interface PolicyInformationCardProps {
  policies?: PolicyRow[];
}

const PolicyInformationCard = ({ policies }: PolicyInformationCardProps = {}) => {
  const isDark = getSessionStore("globalTheme") === THEME.DARK;
  const rows = policies ?? MOCK_POLICIES;

  const headerText = isDark ? "text-[#93C5FD]" : "text-[#1E3A8A]";
  const cardBg = isDark ? "bg-[#2A2A3D]" : "bg-white";
  const thBg = isDark ? "bg-[#1E1E2E]" : "bg-white";
  const thText = isDark ? "text-gray-400" : "text-[#94A3B8]";
  const tdText = isDark ? "text-gray-100" : "text-[#0F172A]";
  const divider = isDark ? "border-[#2E2E42]" : "border-[#F1F5F9]";

  const renderCell = (row: PolicyRow, col: (typeof COLUMNS)[number]) => {
    switch (col.key) {
      case "insuredName":
        return (
          <AvatarWithName
            imageUrl={row.insuredAvatar}
            name={row.insuredName}
            tooltipText={row.insuredName.length > 20 ? row.insuredName : undefined}
          />
        );

      case "policyNumber":
        return (
          <div className="flex items-center gap-2">
            <PolicyBadge />
            <div className="flex items-center gap-1">
              <span className="w-2 h-2 rounded-full bg-[#22C55E] flex-shrink-1" />
              <span className={`text-[13px] font-semibold ${tdText}`}>{row.policyNumber}</span>
            </div>
          </div>
        );

      case "accountValue":
      case "surrenderValue":
      case "benefit":
        return <span className={`text-[13px] font-bold ${tdText}`}>{row[col.key]}</span>;

      case "ownerName":
        return (
          <AvatarWithName
            imageUrl={row.ownerLogo}
            name={row.ownerName}
            tooltipText={row.ownerName.length > 20 ? row.ownerName : undefined}
          />
        );

      default:
        return <span className={`text-[13px] ${tdText}`}>{row[col.key]}</span>;
    }
  };

  return (
    <div className={`w-full rounded-2xl shadow-lg  mt-4 ${cardBg}`}>
      {/* ── Header ── */}
      <p
        className={`text-[13px] font-semibold conthrax-font p-4  flex items-center bg-[#f5f5f5] gap-2  ${headerText}`}
      >
        <PolicyHeaderIcon fill={isDark ? "#93C5FD" : "#1E3A8A"} size="18px" />
        Policy Information
      </p>

      {/* ── Table ── */}
      <div className="w-full overflow-x-auto">
        <table className="w-full border-collapse">
          <thead className="border-b-2 border-[#F5F5F5]">
            <tr className={`${thBg}`}>
              {COLUMNS.map((col) => (
                <th
                  key={col.key}
                  style={{ width: col.width }}
                  className={`px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-wider ${thText} border-b ${divider}`}
                >
                  {col.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, index) => (
              <tr
                key={row.id}
                className={`transition-colors hover:${isDark ? "bg-[#1E1E2E]" : "bg-[#F8FAFC]"} ${
                  index < rows.length - 1 ? `border-b ${divider}` : ""
                }`}
              >
                {COLUMNS.map((col) => (
                  <td key={col.key} className="px-4 py-4">
                    {renderCell(row, col)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default PolicyInformationCard;
